import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ipHolderDelete } from '../../../api/actions/ip-holder/ip-holder-delete';
import { getAllIpHolders } from '../../../api/actions/ip-holder/ip-holders-get-all';
import { AddBreadcrumbsItem } from '../../../components/shared/breadcrumbs/breadcrumbs';
import { Button } from '../../../components/shared/button/button';
import { Grid } from '../../../components/shared/grid/grid';
import { LinkButton } from '../../../components/shared/link-button/link-button';
import { Loader } from '../../../components/shared/loader/loader';
import { DeleteQuestionPopup } from '../../../components/shared/popup/delete-question-popup';
import { compareText } from '../../../helpers/array-helpers';
import { basePerformError } from '../../../helpers/error-helpers';
import { joinPath } from '../../../helpers/path-helpers';
import { IIpHolder } from '../../../models/ip-holder';
import styles from './ip-holders-list.module.scss';

export function IpHoldersList(props: RouteComponentProps) {
    const [loading, setLoading] = useState(true);
    const [ipHolders, setIpHolders] = useState<IIpHolder[]>([]);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [ipHolderToDelete, setIpHolderToDelete] = useState<IIpHolder | undefined>();

    const loadData = React.useCallback(async () => {
        setLoading(true);
        try {
            setIpHolders((await getAllIpHolders()).sort((el1, el2) => compareText(el1, el2, (el) => el.name)))
        }
        catch(err) {
            basePerformError(err, props.history);
        }
        setLoading(false);
    }, [props.history]);

    useEffect(() => {
        loadData().catch(() =>{/** */});
    }, [loadData]);

    const renderButtons = () => (
        <div className={styles.buttons}>
            <Button className="orange" to={joinPath(props.match.url, '_')}>Add IP Holder</Button>
        </div>
    );

    const deleteIpHolder = async () => {
        if(!ipHolderToDelete?.uid) return;
        try {
            await ipHolderDelete(ipHolderToDelete.uid);
            setDeleteDialogVisible(false);
            setIpHolderToDelete(undefined);
            loadData().catch(() =>{/** */});
            toast.success('Item has been successfully deleted');
        }
        catch(err) {
            basePerformError(err, props.history);
        }
    };

    return loading ? (<Loader />) : (<>
        <AddBreadcrumbsItem
            title="IP Holders"
            url={props.match.url}
        />

        <Grid<IIpHolder>
            getPageData={(page, pageSize) => {
                return Promise.resolve({
                    page,
                    count: ipHolders?.length || 0,
                    data: ipHolders?.slice(pageSize * (page - 1), pageSize * page),
                });
            }}

            containerRenderer={(content, pager) => (<>
                <div className={styles.pager}>{pager} {renderButtons()}</div>
                <table cellPadding="0" cellSpacing="0" className={`list ${styles.list}`}>
                    <tbody>
                        <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Actions</th>
                        </tr>
                        {content}
                    </tbody>
                </table>
                <div className={styles.pager}>{pager}</div>
            </>)}

            itemRenderer={(ipHolder, i) => (
                <tr key={ipHolder.uid}>
                    <td>
                        {i + 1}
                    </td>
                    <td>
                        {ipHolder.name}
                    </td>
                    <td>
                        <LinkButton to={joinPath(props.match.url, ipHolder.uid)}>
                            <FontAwesomeIcon icon={faEdit} /> Edit
                        </LinkButton><br />
                        <LinkButton onClick={() => {
                            setIpHolderToDelete(ipHolder);
                            setDeleteDialogVisible(true);
                        }}>
                            <FontAwesomeIcon icon={faTrashAlt} /> Delete
                        </LinkButton>
                    </td>
                </tr>
            )}
        />

        {renderButtons()}

        {deleteDialogVisible && ipHolderToDelete && (
            <DeleteQuestionPopup
                title="IP Holder Deletion"
                question={<>Are you sure you want to delete IP Holder: {ipHolderToDelete.name}?</>}
                onClose={() => {
                    setDeleteDialogVisible(false);
                    setIpHolderToDelete(undefined);
                }}
                onDelete={deleteIpHolder}
                onFinish={() => {
                    setDeleteDialogVisible(false);
                    setIpHolderToDelete(undefined);
                }}
            />
        )}
    </>);
}

